/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  FirebaseMessaging,
  Importance,
  NotificationReceivedEvent,
  NotificationActionPerformedEvent,
} from '@capacitor-firebase/messaging';
import { Badge } from '@capawesome/capacitor-badge';
import { App as CapacitorApp } from '@capacitor/app';
import { auth } from '../firebase';
import { API_BASE_URL } from '../utils/apiBaseUrl.ts';
import { pushPermissionSettled, markPushPermissionSettled } from '../utils/pushPermissionGate.ts';

// Keeps the native app icon badge in sync with the "items sold since last
// opened" count shopify-order-create-notify.ts stamps onto every push's
// data payload. Needed as an explicit client-side call (rather than relying
// solely on APNs' own aps.badge field) because that's iOS-only - Android
// has no equivalent way for a push payload to set the launcher badge
// directly, so Badge.set() here is what actually keeps Android in sync,
// and it's a harmless no-op duplicate of what APNs already did on iOS.
function syncBadgeFromNotificationData(data: Record<string, string> | undefined) {
  if (!data?.unseenSoldCount) {
    return;
  }
  const count = parseInt(data.unseenSoldCount, 10);
  if (Number.isNaN(count)) {
    return;
  }
  // Waits for Firebase Messaging's own (broader) permission request to have
  // been made first - see pushPermissionGate.ts for why this ordering
  // matters. In practice this resolves instantly here, since this listener
  // is only ever registered after that request already completed.
  pushPermissionSettled.then(() => {
    Badge.set({ count }).catch((err) => {
      console.warn('Badge.set() failed:', err);
    });
  });
}

export interface TappedOrderItem {
  title: string;
  variant: string | null;
  quantity: number;
  price: string | null;
}

export interface TappedOrderData {
  orderId: string;
  orderNumber: string;
  orderTotal: string;
  isDraftOrder: boolean;
  items: TappedOrderItem[];
}

/**
 * Registers this device for push notifications and saves the resulting FCM
 * token onto the user's Firestore doc (fcmTokens array, so a user can have
 * multiple devices registered at once). No-ops on web - push notifications
 * only work inside the native iOS/Android app.
 *
 * Uses @capacitor-firebase/messaging rather than @capacitor/push-notifications
 * because the latter returns the raw APNs device token on iOS, not an FCM
 * token - and the backend sends via Firebase Cloud Messaging, which requires
 * genuine FCM registration tokens. This plugin wraps the native Firebase SDK
 * directly, so getToken() does the APNs-to-FCM exchange internally and
 * returns a token that actually works with admin.messaging().send().
 *
 * The token is saved via the save-push-token Netlify function rather than a
 * direct client-side Firestore write - security rules reject direct client
 * writes to the users collection (every other user-doc mutation in this app
 * goes through an Admin-SDK-backed function the same way).
 *
 * Tapping a notification navigates to the sales dashboard and opens a
 * modal with the itemized order details - the items/total/variant data is
 * parsed straight out of the push payload's data fields (see
 * shopify-order-create-notify.ts), not fetched fresh, since it's already
 * delivered with the notification and an order's line items don't change
 * after the fact.
 */
export function usePushNotifications(
  userId: string | null | undefined,
  navigate: (path: string) => void,
  onOrderTapped?: (order: TappedOrderData) => void
): void {
  // navigate/onOrderTapped are only read inside the tap-listener callback
  // below, not used for anything during setup itself - keeping them out of
  // the effect's dependency array (via refs updated on every render
  // instead) means an identity change on either can never tear down and
  // restart the whole permission/channel/token setup. That setup was
  // observed re-running mid-flight on a same-userId re-render, cancelling
  // an in-flight requestPermissions() call that would have succeeded and
  // silently dropping the token save.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const onOrderTappedRef = useRef(onOrderTapped);
  onOrderTappedRef.current = onOrderTapped;

  useEffect(() => {
    if (!userId || !Capacitor.isNativePlatform()) {
      return;
    }

    console.log(`🔔 usePushNotifications effect running for userId=${userId}`);

    let cancelled = false;
    let listenersRegistered = false;
    const listenerHandles: Array<{ remove: () => void }> = [];

    // Android channels + the two notification listeners only need to be
    // set up once per mount (they don't depend on permission status
    // beyond needing it granted the first time this succeeds) - everything
    // else below this is re-run on every attempt, including resumes.
    const registerListenersOnce = async (runId: string) => {
      if (listenersRegistered) {
        return;
      }
      listenersRegistered = true;

      console.log(`🔔[${runId}] Setting up channels/listeners...`);

      // Android 8+ locks a channel's sound in at creation time, so each
      // order-value tier needs its own channel rather than one shared
      // channel - the backend picks the matching channel id by order total
      // when it sends the notification. iOS has no channel concept - its
      // sound is set directly in the push payload instead.
      if (Capacitor.getPlatform() === 'android') {
        const tierChannels = [
          { id: 'order_notifications_low', name: 'Order Notifications (Under $50)', sound: 'cha_ching.mp3' },
          { id: 'order_notifications_mid', name: 'Order Notifications ($50-$100)', sound: 'cha_ching2.mp3' },
          { id: 'order_notifications_high', name: 'Order Notifications ($100-$150)', sound: 'cha_ching3.mp3' },
          { id: 'order_notifications_higher', name: 'Order Notifications ($150-$200)', sound: 'cha_ching4.mp3' },
          { id: 'order_notifications_veryhigh', name: 'Order Notifications ($200-$250)', sound: 'cha_ching5.mp3' },
          { id: 'order_notifications_max', name: 'Order Notifications ($250+)', sound: 'cha_ching_max.mp3' },
        ];
        for (const channel of tierChannels) {
          try {
            await FirebaseMessaging.createChannel({
              ...channel,
              importance: Importance.High,
            });
          } catch (err) {
            console.error(`Failed to create ${channel.id} channel:`, err, err instanceof Error ? err.message : String(err));
          }
        }
      }

      console.log(`🔔[${runId}] Registering notificationReceived listener...`);
      listenerHandles.push(
        await FirebaseMessaging.addListener(
          'notificationReceived',
          (event: NotificationReceivedEvent) => {
            console.log('📬 Push received in foreground:', event);
            syncBadgeFromNotificationData(event.notification?.data as Record<string, string> | undefined);
          }
        )
      );

      console.log(`🔔[${runId}] Registering notificationActionPerformed listener...`);
      listenerHandles.push(
        await FirebaseMessaging.addListener(
          'notificationActionPerformed',
          (event: NotificationActionPerformedEvent) => {
            console.log('👉 Push notification tapped:', event);
            navigateRef.current('/dashboard');

            const data = event.notification?.data as Record<string, string> | undefined;
            syncBadgeFromNotificationData(data);
            if (data?.items && onOrderTappedRef.current) {
              try {
                const items: TappedOrderItem[] = JSON.parse(data.items);
                onOrderTappedRef.current({
                  orderId: data.orderId ?? '',
                  orderNumber: data.orderNumber ?? '',
                  orderTotal: data.orderTotal ?? '0.00',
                  isDraftOrder: data.isDraftOrder === 'true',
                  items,
                });
              } catch (err) {
                console.error('Failed to parse tapped notification items:', err, err instanceof Error ? err.message : String(err));
              }
            }
          }
        )
      );
    };

    // Checks permission, and if granted, (re-)registers the token. Run
    // once on mount AND on every later app resume - a single mount-only
    // check was the actual bug behind "fcmTokens never gets created even
    // after allowing notifications": if permission was denied the first
    // time this ran, there was nothing to ever re-check it again short of
    // fully force-quitting and relaunching the app - toggling the OS
    // Settings switch and foregrounding the app again did nothing, since
    // nothing was listening for that. checkPermissions()/getToken() on an
    // already-granted/already-registered device are cheap no-ops, so
    // re-running this whole thing on every resume is safe.
    const attemptRegistration = async (runId: string) => {
      let permStatus = await FirebaseMessaging.checkPermissions();
      console.log(`🔔[${runId}] checkPermissions ->`, permStatus.receive);

      // checkPermissions() can read a stale 'denied' immediately after the
      // user has actually granted permission - iOS/the bridge hasn't always
      // synced yet. Re-querying via requestPermissions() for any non-granted
      // status (not just 'prompt') catches this: it's a safe no-op either
      // way - iOS shows the system dialog only on a true first-time prompt,
      // confirms 'granted' silently if already allowed, and returns 'denied'
      // again with no dialog if truly denied (it never re-prompts once the
      // user has explicitly declined - but will correctly read 'granted'
      // here once they've changed it in Settings and come back).
      if (permStatus.receive !== 'granted') {
        permStatus = await FirebaseMessaging.requestPermissions();
        console.log(`🔔[${runId}] requestPermissions ->`, permStatus.receive);
      }

      // This request (alert+badge+sound) has now definitely been made,
      // regardless of outcome - safe from here on for anything gated on
      // pushPermissionSettled (see pushPermissionGate.ts) to call a Badge
      // plugin method without racing this broader request.
      markPushPermissionSettled();

      if (permStatus.receive !== 'granted') {
        console.warn(`🔔[${runId}] Push notification permission not granted (final: ${permStatus.receive})`);
        return;
      }

      // Deliberately NOT calling Badge.requestPermissions() here - it was
      // added speculatively (badge permission is bundled into the request
      // above on iOS anyway, confirmed by the badge working correctly
      // without it) and turned out to be actively harmful: iOS only shows
      // a notification sub-toggle (Sounds, Badges, Banners) in Settings
      // for the exact options an app's authorization request included.
      // This was a second, narrower request asking for .badge alone, and
      // it ended up being the one iOS treated as authoritative - the
      // Sounds row disappeared from Settings entirely and pushes stopped
      // playing sound, even though the single combined request above
      // (alert+badge+sound) already covers everything needed.

      if (cancelled) {
        console.log(`🔔[${runId}] cancelled before continuing (a newer effect run already tore this one down)`);
        return;
      }

      await registerListenersOnce(runId);

      console.log(`🔔[${runId}] Calling getToken()...`);

      try {
        const { token } = await FirebaseMessaging.getToken();
        console.log(`🔔[${runId}] ✅ FCM token received`);

        const idToken = await auth.currentUser?.getIdToken();
        const response = await fetch(`${API_BASE_URL}/.netlify/functions/save-push-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`save-push-token failed: ${response.status} ${errorText}`);
        }

        console.log(`🔔[${runId}] ✅ Push token saved`);
      } catch (err) {
        // Plain Error objects don't serialize through Capacitor's native
        // console bridge (message/stack are non-enumerable), so logging
        // the raw object alone prints "{}" and hides the real reason -
        // log the message as its own string argument too.
        console.error(`🔔[${runId}] Failed to get/save push token:`, err, err instanceof Error ? err.message : String(err));
      }
    };

    const runAttempt = (label: string) => {
      const runId = `${label}-${Math.random().toString(36).slice(2, 7)}`;
      // Fire-and-forget - without this .catch(), any error thrown before
      // the inner try/catch (e.g. from checkPermissions/requestPermissions
      // or the addListener calls) becomes an unhandled promise rejection
      // that can go completely unlogged in Capacitor's console bridge.
      attemptRegistration(runId).catch((err) => {
        console.error(`🔔[${runId}] attemptRegistration failed:`, err, err instanceof Error ? err.message : String(err));
      });
    };

    runAttempt('mount');

    const listenerPromise = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        runAttempt('resume');
      }
    });
    listenerPromise.catch((err) => {
      console.warn('🔔 appStateChange addListener failed:', err);
    });

    return () => {
      console.log('🔔 usePushNotifications effect cleaning up');
      cancelled = true;
      listenerHandles.forEach((handle) => handle.remove());
      listenerPromise.then((handle) => handle.remove()).catch(() => {});
    };
  }, [userId]);
}
